// Serializes prompt turns against out-of-band driver swaps: runner command
// dispatch is fire-and-forget, so an attach_instance-driven driver recreate can
// otherwise interleave with — and kill the driver under — an in-flight turn.

/**
 * Coordinates the two directions of the driver-swap / prompt-turn race:
 *
 * - swap-during-turn: a swap {@link awaitActiveTurn}s so the outgoing driver is
 *   never killed while a turn is still awaiting its `prompt()`.
 * - prompt-during-swap: a prompt {@link awaitSwap}s so it binds to the fresh
 *   driver instead of one being disposed.
 *
 * A single instance is scoped to one agent server. All methods are safe to call
 * even when nothing is in flight (they resolve immediately).
 */
export class DriverSwapGate {
  /** Resolves when the in-flight driver recreate settles (resolve or reject). */
  private activeDriverSwap: Promise<void> | null = null;
  /** FIFO chain; resolves once every started turn has settled. */
  private turnTail: Promise<void> = Promise.resolve();

  /**
   * Mark a turn as in flight. Call at the real `driver.prompt()` dispatch — not
   * earlier — so a same-handler restart (e.g. restaging skills before the turn)
   * cannot self-deadlock by awaiting the very turn that spawned it. Returns an
   * idempotent `endTurn`; call it in a `finally` around the dispatch.
   */
  beginTurn(): () => void {
    let resolveTurn!: () => void;
    const turn = new Promise<void>((resolve) => {
      resolveTurn = resolve;
    });
    this.turnTail = this.turnTail.then(() => turn);
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      resolveTurn();
    };
  }

  /**
   * Await any in-flight turn before tearing a driver down. Awaits the FIFO tail
   * (not a single "latest turn" field) so an earlier turn that outlives a later
   * one still holds the swap off — the tail resolves only once every started
   * turn has settled. Never throws: a rejected turn is still a settled turn, and
   * the swap may proceed (the driver is idle either way).
   */
  async awaitActiveTurn(): Promise<void> {
    try {
      await this.turnTail;
    } catch {
      // A failed turn is still a settled turn — swap may proceed.
    }
  }

  /**
   * Register the in-flight recreate promise so concurrent prompts defer to it.
   * Auto-clears when `recreate` settles (resolve OR reject) so a failed recreate
   * never blocks prompts forever.
   */
  beginSwap(recreate: Promise<void>): void {
    this.activeDriverSwap = recreate;
    const clear = () => {
      if (this.activeDriverSwap === recreate) this.activeDriverSwap = null;
    };
    recreate.then(clear, clear);
  }

  /**
   * Await the in-flight swap before reading the live driver. Re-checks in a loop
   * so a second swap starting during the wait is also awaited.
   */
  async awaitSwap(): Promise<void> {
    while (this.activeDriverSwap) {
      try {
        await this.activeDriverSwap;
      } catch {
        // A failed swap must not strand the waiting prompt.
      }
    }
  }
}
